import { effect, untracked } from '@angular/core';
import {
  signalStoreFeature,
  type,
  withHooks,
  withMethods,
} from '@ngrx/signals';
import type {
  EntityFormSlice,
  EntityIdState,
  EntitySource,
} from './entity-form.types';
import { resetFormFromEntity } from './reset-form-from-entity.util';

type EntityIdStoreSlice = {
  entityId: () => number | null;
};

function watchEntityIdForLoad<Entity>(
  store: EntityIdStoreSlice,
  entityStore: EntitySource<Entity>
): void {
  effect(() => {
    const entityId = store.entityId();
    if (entityId !== null && entityId > 0) {
      untracked(() => entityStore.loadById(entityId));
    }
  });
}

function watchEntityForFormReset<Entity, FormModel>(
  store: EntityFormSlice<FormModel>,
  entityStore: EntitySource<Entity>,
  mapEntityToFormValue: (entity: Entity | undefined) => FormModel
): void {
  effect(() => {
    const entity = entityStore.entity();
    untracked(() => {
      if (entity) {
        resetFormFromEntity({
          entity,
          formSlice: store,
          mapEntityToFormValue,
        });
      }
    });
  });
}

/**
 * Wires an entity edit form to a single-entity store: load on id change,
 * sync the form when the entity arrives, and expose `resetFromEntity()` /
 * `undo()`.
 *
 * Compose after {@link withEntityId} and {@link withSignalForm}.
 *
 * @template Entity The loaded entity type.
 * @template FormModel The signal form model type.
 */
export function withEntityFormFromSource<Entity, FormModel>(config: {
  entityStore: () => EntitySource<Entity>;
  mapEntityToFormValue: (entity: Entity | undefined) => FormModel;
}) {
  return signalStoreFeature(
    {
      props: type<EntityFormSlice<FormModel>>(),
      state: type<EntityIdState>(),
    },
    withMethods((store, entityStore = config.entityStore()) => {
      const reset = () => {
        resetFormFromEntity({
          entity: entityStore.entity(),
          formSlice: store,
          mapEntityToFormValue: config.mapEntityToFormValue,
        });
      };

      return {
        resetFromEntity() {
          reset();
        },
        undo() {
          reset();
        },
      };
    }),
    withHooks((store) => {
      const entityStore = config.entityStore();

      return {
        onInit() {
          watchEntityIdForLoad(store, entityStore);
          watchEntityForFormReset(
            store,
            entityStore,
            config.mapEntityToFormValue
          );
        },
      };
    })
  );
}
